import { Fragment, useEffect } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { RadioGroup } from '@roger/ui-components';
import {
  BespokeRadioRowProvider,
  useBespokeSectionContext,
} from '../../primitives';
import { useQuestionFieldDisabled } from '../../primitives/useQuestionFieldDisabled';
import type { RepeatBindings } from '../../primitives';
import {
  joinClasses,
  BESPOKE_TABLE_BODY_ROW_CLASS,
  BESPOKE_TABLE_CELL_CLASS,
  BESPOKE_TABLE_COL_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_CELL_CLASS,
  BESPOKE_TABLE_COMPACT_COL_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_ROW_HEADER_CLASS,
  BESPOKE_TABLE_COMPACT_ROW_LABEL_CLASS,
  BESPOKE_TABLE_DENSITY_COMPACT_PAD_CLASS,
  BESPOKE_TABLE_DENSITY_DEFAULT_PAD_CLASS,
  BESPOKE_TABLE_DENSITY_LOOSE_PAD_CLASS,
  BESPOKE_TABLE_ELEMENT_CLASS,
  BESPOKE_TABLE_ROW_HEADER_CLASS,
  BESPOKE_TABLE_ROW_LABEL_CLASS,
  BESPOKE_TABLE_SHELL_CLASS,
  BESPOKE_TABLE_SHELL_STRUCTURAL_CLASS,
  BESPOKE_TABLE_STRUCTURAL_CELL_CLASS,
  BESPOKE_TABLE_STRUCTURAL_COL_HEADER_CLASS,
  BESPOKE_TABLE_STRUCTURAL_ROW_HEADER_CLASS,
  BESPOKE_TABLE_STRUCTURAL_ROW_LABEL_CLASS,
} from '../../primitives/fieldClassSlots';
import type { FormValue } from '../../../types';
import { QidTag } from '../../../../dev/QidTag';
import { useLayoutDerived } from '../LayoutDerivedContext';
import { layoutInlineStyle } from '../layoutInlineStyle';
import {
  holdsOnlyUnservedFields,
  reportHiddenFields,
  unservedFieldQids,
} from '../reportHiddenFields';
import {
  tableSlotNodes,
  walkLayoutStructureChildren,
} from '../layoutStructureChildLists';
import type { LayoutNodeChildProps } from '../nodeRenderProps';
import type {
  LayoutInlineStyle,
  LayoutNode,
  TableChromeMode,
  TableNode,
  TableVariant,
} from '../types';
import { conditionalWhenSatisfied } from '../whenClause';

type TableRowNode = TableNode['rows'][number];

type TableSlotChrome = {
  shell: string;
  rowHeader: string;
  colHeader: string;
  rowLabel: string;
  cell: string;
  bodyRow: string | undefined;
};

function densityPad(variant: TableVariant): string {
  switch (variant) {
    case 'compact':
      return BESPOKE_TABLE_DENSITY_COMPACT_PAD_CLASS;
    case 'loose':
      return BESPOKE_TABLE_DENSITY_LOOSE_PAD_CLASS;
    default:
      return BESPOKE_TABLE_DENSITY_DEFAULT_PAD_CLASS;
  }
}

/** @deprecated `chrome` defaults to `legacy` for back-compat; prefer `"none"`. */
function resolveChromeMode(
  chrome: TableChromeMode | undefined
): TableChromeMode {
  return chrome ?? 'legacy';
}

function resolveVariant(variant: TableVariant | undefined): TableVariant {
  return variant ?? 'default';
}

/**
 * Built-in slot classes. `chrome` is deprecated — `legacy` keeps historical
 * primary-wash packs; `none` is structural + density only so author
 * `*ClassName` / `*Style` own the look (same model as `stack` / `card` / `box`).
 */
function tableSlotChrome(
  variant: TableVariant,
  chrome: TableChromeMode
): TableSlotChrome {
  if (chrome === 'none') {
    const pad = densityPad(variant);
    return {
      shell: BESPOKE_TABLE_SHELL_STRUCTURAL_CLASS,
      rowHeader: joinClasses(BESPOKE_TABLE_STRUCTURAL_ROW_HEADER_CLASS, pad),
      colHeader: joinClasses(BESPOKE_TABLE_STRUCTURAL_COL_HEADER_CLASS, pad),
      rowLabel: joinClasses(BESPOKE_TABLE_STRUCTURAL_ROW_LABEL_CLASS, pad),
      cell: joinClasses(BESPOKE_TABLE_STRUCTURAL_CELL_CLASS, pad),
      bodyRow: undefined,
    };
  }

  // legacy — variant compact selects compact pack; default/loose → default pack
  if (variant === 'compact') {
    return {
      shell: BESPOKE_TABLE_SHELL_CLASS,
      rowHeader: BESPOKE_TABLE_COMPACT_ROW_HEADER_CLASS,
      colHeader: BESPOKE_TABLE_COMPACT_COL_HEADER_CLASS,
      rowLabel: BESPOKE_TABLE_COMPACT_ROW_LABEL_CLASS,
      cell: BESPOKE_TABLE_COMPACT_CELL_CLASS,
      bodyRow: BESPOKE_TABLE_BODY_ROW_CLASS,
    };
  }

  return {
    shell: BESPOKE_TABLE_SHELL_CLASS,
    rowHeader: BESPOKE_TABLE_ROW_HEADER_CLASS,
    colHeader: BESPOKE_TABLE_COL_HEADER_CLASS,
    rowLabel: BESPOKE_TABLE_ROW_LABEL_CLASS,
    cell: BESPOKE_TABLE_CELL_CLASS,
    bodyRow: BESPOKE_TABLE_BODY_ROW_CLASS,
  };
}

function mergeInlineStyles(
  ...parts: (LayoutInlineStyle | undefined)[]
): CSSProperties | undefined {
  const merged: LayoutInlineStyle = {};
  for (const part of parts) {
    if (part == null) continue;
    Object.assign(merged, part);
  }
  return layoutInlineStyle(Object.keys(merged).length > 0 ? merged : undefined);
}

type RenderLayoutNode = (props: {
  node: LayoutNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) => ReactNode;

const DIRECT_CELL_CLICK_TARGET_SELECTOR =
  'input, button, select, textarea, label, a, [role="button"], [role="checkbox"], [role="radio"], [data-no-cell-click]';

const CELL_CHOICE_CONTROL_SELECTOR =
  'input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled), [role="checkbox"]:not([aria-disabled="true"]):not(:disabled), [role="radio"]:not([aria-disabled="true"]):not(:disabled)';

function findRadioQidInCells(cells: LayoutNode[]): string | undefined {
  for (const cell of cells) {
    if (cell.type === 'radio-option') {
      return cell.qid;
    }
  }
  return undefined;
}

// When a cell has a single radio or checkbox, clicking anywhere in the cell selects it
// so users don't have to aim at the small control.
function handleChoiceTableCellClick(event: MouseEvent<HTMLTableCellElement>) {
  const target = event.target;

  if (!(target instanceof Element)) {
    return;
  }

  if (target.closest(DIRECT_CELL_CLICK_TARGET_SELECTOR)) {
    return;
  }

  const controls = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      CELL_CHOICE_CONTROL_SELECTOR
    )
  );

  if (controls.length !== 1) {
    return;
  }

  controls[0].click();
}

/**
 * Render a header or row-label slot that may be plain text or layout nodes.
 *
 * Both spellings stay valid: every layout written before nodes were allowed
 * passes a `string` here, and must keep rendering byte-identically.
 */
function TableTextSlot({
  slot,
  answers,
  repeatBindings,
  renderNode,
}: {
  slot: string | LayoutNode[] | undefined;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
  renderNode: RenderLayoutNode;
}) {
  if (slot == null) return null;
  if (typeof slot === 'string') return <>{slot}</>;
  // Keyed fragments, not wrapper elements: `RenderNodes` renders every other
  // node list bare, and a wrapper here would become the flex item whenever the
  // cell is styled as a flex row, so a node's own sizing class would stop
  // applying. (`Table` cannot import `RenderNodes` -- that module imports
  // `Table` -- which is why `renderNode` arrives as a prop.)
  return (
    <>
      {slot.map((node, i) => (
        <Fragment key={i}>
          {renderNode({ node, answers, repeatBindings })}
        </Fragment>
      ))}
    </>
  );
}

/**
 * True when any node in the subtree renders words the user can read.
 *
 * A control's own `labelOverride` does not count: it belongs to the control,
 * is often hidden (`hideLabel`), and rides along with it when the cell is
 * clicked. Only copy the cell exists to *display* is read here.
 */
function hasReadableText(nodes: LayoutNode[]): boolean {
  return nodes.some(node => {
    if (
      node.type === 'static' ||
      node.type === 'text' ||
      node.type === 'section-label'
    ) {
      if (node.text.trim()) return true;
    }
    if (node.type === 'display' || node.type === 'external-answer') {
      if (node.label?.trim()) return true;
    }
    if (node.type === 'external-answer' && node.empty?.trim()) return true;
    let found = false;
    walkLayoutStructureChildren(node, children => {
      found = found || hasReadableText(children);
    });
    return found;
  });
}

/**
 * True when the stub cell holds only controls, so clicking its whitespace can
 * activate the one inside it the way an answer cell already does.
 *
 * Gated on there being no text *anywhere* in the label: a label cell's usual
 * job is to be *read*, and making the row's own wording click-to-toggle would
 * turn selecting it into an answer change. The words are as likely to sit
 * inside a wrapper as at the top level, so the check has to recurse.
 */
function rowLabelIsControlOnly(
  label: string | LayoutNode[] | undefined
): boolean {
  if (!Array.isArray(label)) return false;
  return label.length > 0 && !hasReadableText(label);
}

function TableRow({
  row,
  answers,
  repeatBindings,
  renderNode,
  tableNode,
  chrome,
}: {
  row: TableRowNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
  renderNode: RenderLayoutNode;
  tableNode: TableNode;
  chrome: TableSlotChrome;
}) {
  const { onAnswerChange, schemaMap } = useBespokeSectionContext();
  const radioQid = findRadioQidInCells(row.cells);
  const question = radioQid != null ? schemaMap.get(radioQid) : undefined;
  const rowRadioDisabled = useQuestionFieldDisabled(question);
  const selectedValue = radioQid != null ? String(answers[radioQid] ?? '') : '';

  const cells = row.cells.map((cell, ci) => (
    <td
      key={ci}
      className={joinClasses(
        chrome.cell,
        tableNode.cellClassName,
        row.cellClassName
      )}
      style={mergeInlineStyles(tableNode.cellStyle, row.cellStyle)}
      onClick={handleChoiceTableCellClick}
    >
      {renderNode({ node: cell, answers, repeatBindings })}
    </td>
  ));

  const tr = (
    <tr
      className={joinClasses(
        chrome.bodyRow,
        tableNode.rowClassName,
        row.rowClassName
      )}
      style={mergeInlineStyles(tableNode.rowStyle, row.rowStyle)}
    >
      <td
        className={joinClasses(
          chrome.rowLabel,
          tableNode.rowLabelClassName,
          row.rowLabelClassName
        )}
        style={mergeInlineStyles(tableNode.rowLabelStyle, row.rowLabelStyle)}
        onClick={
          rowLabelIsControlOnly(row.label)
            ? handleChoiceTableCellClick
            : undefined
        }
      >
        <TableTextSlot
          slot={row.label}
          answers={answers}
          repeatBindings={repeatBindings}
          renderNode={renderNode}
        />
        {radioQid != null && <QidTag qid={radioQid} />}
      </td>
      {radioQid != null ? (
        <BespokeRadioRowProvider>{cells}</BespokeRadioRowProvider>
      ) : (
        cells
      )}
    </tr>
  );

  if (radioQid == null) {
    return tr;
  }

  return (
    <RadioGroup
      asChild
      className='table-row'
      value={selectedValue}
      disabled={rowRadioDisabled}
      onValueChange={v => {
        if (rowRadioDisabled || radioQid == null) return;
        onAnswerChange(radioQid, v);
      }}
    >
      {tr}
    </RadioGroup>
  );
}

export function Table({
  node,
  answers,
  repeatBindings,
  renderNode,
}: LayoutNodeChildProps<TableNode> & {
  renderNode: RenderLayoutNode;
}) {
  const { schemaMap, hideFieldsMissingFromSchema } = useBespokeSectionContext();
  const derived = useLayoutDerived();
  const variant = resolveVariant(node.variant);
  const chromeMode = resolveChromeMode(node.chrome);
  const chrome = tableSlotChrome(variant, chromeMode);
  const visibleRows = node.rows
    .map((row, ri) => ({ row, ri }))
    .filter(
      ({ row }) =>
        conditionalWhenSatisfied(
          row,
          answers,
          repeatBindings,
          derived,
          schemaMap
        ) && !(hideFieldsMissingFromSchema && rowAsksNothing(row, schemaMap))
    );
  useEffect(() => {
    if (!hideFieldsMissingFromSchema) return;
    reportHiddenFields(
      node.rows
        .filter(row => rowAsksNothing(row, schemaMap))
        .flatMap(row => unservedFieldQids(rowNodes(row), schemaMap))
    );
  }, [hideFieldsMissingFromSchema, node.rows, schemaMap]);

  return (
    <div
      className={joinClasses(chrome.shell, node.className)}
      style={layoutInlineStyle(node.style)}
    >
      <table
        className={joinClasses(
          BESPOKE_TABLE_ELEMENT_CLASS,
          node.tableClassName
        )}
        style={layoutInlineStyle(node.tableStyle)}
      >
        <thead>
          <tr>
            <th
              className={joinClasses(chrome.rowHeader, node.rowHeaderClassName)}
              style={layoutInlineStyle(node.rowHeaderStyle)}
            >
              <TableTextSlot
                slot={node.rowHeader ?? ''}
                answers={answers}
                repeatBindings={repeatBindings}
                renderNode={renderNode}
              />
            </th>
            {node.colHeaders.map((h, hi) => (
              <th
                key={hi}
                className={joinClasses(
                  chrome.colHeader,
                  node.colHeaderClassName
                )}
                style={layoutInlineStyle(node.colHeaderStyle)}
              >
                <TableTextSlot
                  slot={h}
                  answers={answers}
                  repeatBindings={repeatBindings}
                  renderNode={renderNode}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map(({ row, ri }) => (
            <TableRow
              key={ri}
              row={row}
              answers={answers}
              repeatBindings={repeatBindings}
              renderNode={renderNode}
              tableNode={node}
              chrome={chrome}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A row whose fields the form asks none of, label included; print drops it too. */
function rowAsksNothing(
  row: TableRowNode,
  schemaMap: ReadonlyMap<string, unknown>
): boolean {
  return holdsOnlyUnservedFields(rowNodes(row), schemaMap);
}

function rowNodes(row: TableRowNode): LayoutNode[] {
  return [...tableSlotNodes(row.label), ...(row.cells ?? [])];
}
